// Месяц периода. Правило 9: календарь всегда подписан датой — «Август 2026», а не «текущий
// период». Месяц живёт в адресе (?f.period=2026-08), поэтому ссылка на норматив за май
// открывается у коллеги маем, а не его сегодняшним месяцем.
//
// Лежит в общем слое, а не в разделе объектов: месяцем живут вкладка норматива в карточке
// объекта и сетка табеля. Второй разбор рядом означал бы два способа прочитать один и тот же
// адрес — и однажды они разойдутся на границе года.

export interface Period {
  year: number;
  month: number;
}

/** Границы, которые принимает сервер (contracts/openapi: year 2000..2100, month 1..12). */
const MIN_YEAR = 2000;
const MAX_YEAR = 2100;

const MONTH_TITLES: readonly string[] = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

/** Те же месяцы в предложном падеже: «в мае», «в октябре». */
const MONTH_IN: readonly string[] = [
  'январе',
  'феврале',
  'марте',
  'апреле',
  'мае',
  'июне',
  'июле',
  'августе',
  'сентябре',
  'октябре',
  'ноябре',
  'декабре',
];

/** Те же месяцы в родительном падеже: «с ноября», «до марта». */
const MONTH_OF: readonly string[] = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

const PERIOD_PATTERN = /^(\d{4})-(\d{2})$/;

/** Месяц из адреса, если он записан верно, иначе `null`. Единственное место разбора «2026-08». */
function разобратьМесяц(raw: string | undefined): Period | null {
  const match = raw === undefined ? null : PERIOD_PATTERN.exec(raw);
  if (match === null) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  return year >= MIN_YEAR && year <= MAX_YEAR && month >= 1 && month <= 12 ? { year, month } : null;
}

export function parsePeriod(raw: string | undefined, now: Date = new Date()): Period {
  return разобратьМесяц(raw) ?? { year: now.getFullYear(), month: now.getMonth() + 1 };
}

/**
 * Месяц, который человек ВЫБРАЛ и который записан в адресе, или `null`, когда в адресе месяца
 * нет (или он записан криво).
 *
 * `parsePeriod` в обоих случаях отдаёт месяц, и по его ответу не понять, просили ли этот месяц
 * или он взят по умолчанию. Вкладкам «Кому выдано» и «На что потрачено» различие нужно: месяц,
 * заданный явно, главнее любой подсказки, а без него сервер ищет последний, где есть данные.
 */
export function месяцИзАдреса(raw: string | undefined): Period | null {
  return разобратьМесяц(raw);
}

/** «2026-08» — то, что уходит в адрес. */
export function formatPeriodKey(period: Period): string {
  return `${period.year}-${String(period.month).padStart(2, '0')}`;
}

/** «Август 2026» — то, что читает человек. */
export function formatPeriodTitle(period: Period): string {
  return `${MONTH_TITLES[period.month - 1] ?? String(period.month)} ${period.year}`;
}

/**
 * «мае 2026», «октябре 2026»: месяц в предложном падеже, для фразы «в мае 2026».
 *
 * Слово «в» ставит вызывающий: фраза может начинаться с него с большой буквы («В октябре 2026
 * выдач ещё не было») или стоять посреди предложения.
 */
export function месяцВПредложном(period: Period): string {
  return `${MONTH_IN[period.month - 1] ?? String(period.month)} ${period.year}`;
}

/**
 * «ноября 2025», «марта 2026»: месяц в родительном падеже, для фразы «с ноября 2025».
 *
 * Для «по март 2026» родительный не нужен: после «по» стоит винительный, а у месяцев он совпадает
 * с именительным (`formatPeriodTitle` в нижнем регистре).
 */
export function месяцВРодительном(period: Period): string {
  return `${MONTH_OF[period.month - 1] ?? String(period.month)} ${period.year}`;
}

/** Сдвиг на месяц вперёд или назад, с переходом через год. */
export function shiftPeriod(period: Period, months: number): Period {
  const zeroBased = period.year * 12 + (period.month - 1) + months;
  const year = Math.floor(zeroBased / 12);
  const month = zeroBased - year * 12 + 1;
  if (year < MIN_YEAR || year > MAX_YEAR) {
    return period;
  }
  return { year, month };
}

/**
 * Месяц, за который сейчас выдают зарплату, — прошлый.
 *
 * Ведомость считают после конца месяца: заказчик выдаёт зарплату за прошлый месяц с 10 по 30
 * число (переписка 23.09.2026). «Дела» проверяют тарифы именно за этот месяц: за текущий
 * ведомостей ещё не бывает, и «не посчитается» про него — не новость. На нём же открываются
 * вкладки «Ведомости» и «Удержания» (`месяцИзАдресаИлиРасчётный`).
 *
 * Первый экран на него НЕ переключён и месяца не листает — намеренно, проверено снимком
 * 23.09.2026. Неподтверждённый подотчёт сервер без месяца считает весь сегодняшний (23 человека,
 * как в списке слева), а с месяцем — только движения этого месяца (4 человека в сентябре, 15 в
 * августе). Любой выбранный месяц развёл бы главное число со списком людей под ним. Другие
 * месяцы с теми же плитками листаются в сводке раздела «Расчёт».
 */
export function расчётныйМесяц(сейчас: Date): Period {
  return shiftPeriod({ year: сейчас.getFullYear(), month: сейчас.getMonth() + 1 }, -1);
}

/**
 * Месяц экрана: из адреса, а когда в адресе его нет (или он записан криво) — расчётный.
 *
 * Так открываются «Ведомости» и «Удержания»: человек приходит сюда платить за прошлый месяц, а
 * экран на текущем встречал его пустотой (ведомостей за начавшийся месяц ещё нет), и до нужного
 * надо было листать стрелкой. Разбор один на оба экрана: два способа прочитать один и тот же
 * адрес однажды разойдутся.
 *
 * Месяц, выбранный руками, всегда пишется в адрес (`?f.period=…`) и потому переживает и
 * перезагрузку, и полночь на стыке месяцев: расчётный месяц считается только для адреса без
 * месяца.
 */
export function месяцИзАдресаИлиРасчётный(
  raw: string | undefined,
  сейчас: Date = new Date(),
): Period {
  return разобратьМесяц(raw) ?? расчётныйМесяц(сейчас);
}
